import type {QueryClient} from "@tanstack/react-query";
import {OttopilotBtlActivityStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotBtlActivitiesWorkspaceCapability/domain/OttopilotBtlActivity";
import type {BtlActivityTemplateData} from "~/domainModels/btlActivityTemplates";
import {OttopilotEventStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import {eventContainerStatusToDisplayName} from "~/utilities/domainStatusDisplayNames";
import type {OrganizationalUnitTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/organizationalUnitsWorkspaceCapability/domain/OrganizationalUnitTemplate";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {getDhiraaguEventContainerDispositions, lastEventTypes} from "~/domainModels/workspaceSpecific/dhiraagu";
import {getEmotoradDispositionL1Label} from "~/domainModels/workspaceSpecific/emotorad";
import {amaronFuelTypes, amaronServiceRequestedTypes, resolveAmaronDashboardDispositionDisplayLabel} from "~/domainModels/workspaceSpecific/amaron";
import {
    DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES,
    DASHBOARD_DATASET_DISPLAY_NAMES,
    type DisplayNameOrAliases,
} from "~/workspaceConfig/hostedPages/dashboard/-constants";
import type {Dataset} from "@vesta/vos-types/modules/workspaceLevel/domain/Dataset";
import type {DataResolverProps, RawData} from "@vesta/vos-types/modules/workspaceLevel/domain/RawDataType";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import {uniqueUuid} from "@vesta/unique/unique";
import {getBtlActivityTemplates} from "~/utilities/apis/api";
import {CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT, getOrganizationalUnitsWithCaching, getProductCatalogues} from "~/utilities/queries";
import {processOttopilotStringTemplate} from "@vesta/vos-types/modules/workspaceLevel/domain/OttopilotStringTemplate";

function eventStatusToDisplayName(eventStatus: OttopilotEventStatus): string {
    switch (eventStatus) {
        case OttopilotEventStatus.active: {
            return "Active";
        }
        case OttopilotEventStatus.archived: {
            return "Archived";
        }
    }
}

function btlEventStatusToDisplayName(btlEventStatus: OttopilotBtlActivityStatus): string {
    switch (btlEventStatus) {
        case OttopilotBtlActivityStatus.scheduled: {
            return "Scheduled";
        }
        case OttopilotBtlActivityStatus.cancelled: {
            return "Cancelled";
        }
        case OttopilotBtlActivityStatus.completed: {
            return "Completed";
        }
        case OttopilotBtlActivityStatus.draft: {
            return "Draft";
        }
        case OttopilotBtlActivityStatus.active: {
            return "Live";
        }
    }
}

export function normalizeDisplayName(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");
}

export function getDatasetColumnIdFromDisplayNames(dataset: Dataset, displayNameOrAliases: DisplayNameOrAliases): Uuid | null {
    const aliases = Array.isArray(displayNameOrAliases) ? displayNameOrAliases : [displayNameOrAliases];
    const normalizedAliases = aliases.map(normalizeDisplayName);
    const matches = dataset.columns.filter((column) => normalizedAliases.includes(normalizeDisplayName(column.displayName)));
    if (matches.length === 0) {
        return null;
    }
    if (matches.length > 1) {
        throw new Error(`Ambiguous column display name resolution for aliases: ${aliases.join(", ")}`);
    }
    return matches[0]!.id;
}

function getDatasetColumnIdsFromDisplayNames(dataset: Dataset, displayNameOrAliases: DisplayNameOrAliases): Uuid[] {
    const aliases = Array.isArray(displayNameOrAliases) ? displayNameOrAliases : [displayNameOrAliases];
    const normalizedAliases = aliases.map(normalizeDisplayName);
    return dataset.columns.filter((column) => normalizedAliases.includes(normalizeDisplayName(column.displayName))).map((column) => column.id);
}

type DashboardDatasetDisplayNamesByKey = typeof DASHBOARD_DATASET_DISPLAY_NAMES;
type DashboardDatasetColumnDisplayNamesByKey = typeof DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES;
export type DashboardDatasetKey = keyof DashboardDatasetDisplayNamesByKey;
type DashboardDatasetsByKey = {
    -readonly [K in DashboardDatasetKey]: Dataset;
};
type DashboardDatasetColumnIdsByKey = {
    -readonly [K in DashboardDatasetKey]: {
        -readonly [P in keyof DashboardDatasetColumnDisplayNamesByKey[K]]: Uuid;
    };
};

export type ResolvedDashboardDatasetsAndColumns<K extends DashboardDatasetKey = DashboardDatasetKey> = {
    datasets: Pick<DashboardDatasetsByKey, K>;
    columnIds: Pick<DashboardDatasetColumnIdsByKey, K>;
};

export type AnyResolvedDashboardDatasetsAndColumns = {
    datasets: Partial<DashboardDatasetsByKey>;
    columnIds: Partial<DashboardDatasetColumnIdsByKey>;
};

export function resolveDashboardDatasetsAndColumns<const K extends readonly DashboardDatasetKey[]>(
    datasets: Dataset[],
    datasetKeys: K,
): ResolvedDashboardDatasetsAndColumns<K[number]> {
    const datasetMap = new Map<string, Dataset>();
    for (const dataset of datasets) {
        datasetMap.set(normalizeDisplayName(dataset.displayName), dataset);
    }

    const missingDatasets: string[] = [];
    const missingColumns: string[] = [];
    const resolvedDatasets: Partial<DashboardDatasetsByKey> = {};
    const resolvedColumnIds: Record<string, Record<string, Uuid>> = {};

    for (const datasetKey of datasetKeys) {
        const datasetDisplayName = DASHBOARD_DATASET_DISPLAY_NAMES[datasetKey];
        const dataset = datasetMap.get(normalizeDisplayName(datasetDisplayName));
        if (dataset === undefined) {
            missingDatasets.push(`${datasetKey} (${datasetDisplayName})`);
            continue;
        }
        resolvedDatasets[datasetKey] = dataset;
    }

    for (const datasetKey of datasetKeys) {
        const columnDisplayNames = DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES[datasetKey];
        const dataset = resolvedDatasets[datasetKey];
        if (dataset === undefined) {
            continue;
        }

        const datasetColumnIds: Record<string, Uuid> = {};
        const ambiguousColumns: string[] = [];

        for (const [columnKey, displayNameOrAliases] of Object.entries(columnDisplayNames) as Array<[string, DisplayNameOrAliases]>) {
            const columnIds = getDatasetColumnIdsFromDisplayNames(dataset, displayNameOrAliases);
            if (columnIds.length === 0) {
                missingColumns.push(`${String(datasetKey)}.${columnKey}`);
                continue;
            }

            if (columnIds.length > 1) {
                ambiguousColumns.push(`${String(datasetKey)}.${columnKey}`);
            }

            datasetColumnIds[columnKey] = columnIds[0]!;
        }

        resolvedColumnIds[String(datasetKey)] = datasetColumnIds;

        if (ambiguousColumns.length > 0) {
            // Prefer first matched column id deterministically when backend has duplicates.
            // Keep this as non-fatal so loader remains resilient while still surfacing missing columns.
        }
    }

    if (missingDatasets.length > 0 || missingColumns.length > 0) {
        const errors: string[] = ["Dashboard dataset/column resolution failed."];
        if (missingDatasets.length > 0) {
            errors.push(`Missing datasets: ${missingDatasets.join(", ")}`);
        }
        if (missingColumns.length > 0) {
            errors.push(`Missing columns: ${missingColumns.join(", ")}`);
        }
        throw new Error(errors.join(" "));
    }

    return {
        datasets: resolvedDatasets as Pick<DashboardDatasetsByKey, K[number]>,
        columnIds: resolvedColumnIds as Pick<DashboardDatasetColumnIdsByKey, K[number]>,
    };
}

function transformColumnValues(
    data: RawData,
    columnDisplayNames: string | readonly string[],
    transformer: (value: unknown) => string,
    newColumnDisplayName?: string,
): RawData {
    const names = Array.isArray(columnDisplayNames) ? columnDisplayNames : [columnDisplayNames];
    const normalised = (names as string[]).map((n) => normalizeDisplayName(n));
    const colMeta = data.columnMetadata.find((c) => normalised.includes(normalizeDisplayName(c.displayName)));
    if (colMeta === undefined) {
        throw new Error(`Column metadata not found for: ${(names as string[]).join(", ")}`);
    }
    const colIndex = colMeta.columnIndex;
    const dataPoints = data.dataPoints.map((row) => {
        const cell = row[colIndex];
        if (cell === undefined) {
            throw new Error(`Missing cell at column index ${colIndex}`);
        }
        const newRow = [...row];
        newRow[colIndex] = {value: transformer(cell.value)};
        return newRow;
    });
    const columnMetadata =
        newColumnDisplayName !== undefined
            ? data.columnMetadata.map((c) => (c.columnIndex === colIndex ? {...c, displayName: newColumnDisplayName} : c))
            : data.columnMetadata;
    return {columnMetadata, dataPoints};
}

/**
 * Groups rows that share the same value across the given dimension columns and sums numeric metric cells.
 * Used after relabeling (e.g. leaf disposition id → L1 group name) so the chart renders one slice per group
 * instead of N slices with duplicate labels.
 */
function aggregateRawDataByDimensions(data: RawData, dimensionColumnIds: ReadonlyArray<Uuid>): RawData {
    const dimensionIdSet = new Set<string>(dimensionColumnIds);
    const dimensionIndices = data.columnMetadata.filter((c) => dimensionIdSet.has(c.id)).map((c) => c.columnIndex);

    if (dimensionIndices.length === 0) {
        return data;
    }

    const dimensionIndexSet = new Set(dimensionIndices);
    const groups = new Map<string, Array<{value: string | number | null}>>();

    for (const row of data.dataPoints) {
        const key = dimensionIndices.map((idx) => String(row[idx]?.value ?? "")).join("\u0000");

        const existing = groups.get(key);
        if (existing === undefined) {
            groups.set(
                key,
                row.map((cell) => ({value: cell.value})),
            );
            continue;
        }

        for (let i = 0; i < row.length; i++) {
            if (dimensionIndexSet.has(i)) {
                continue;
            }
            const incoming = row[i]?.value;
            const current = existing[i]?.value;
            if (typeof incoming === "number" && typeof current === "number") {
                existing[i] = {value: current + incoming};
            } else if (typeof incoming === "number" && current === null) {
                existing[i] = {value: incoming};
            }
        }
    }

    return {
        columnMetadata: data.columnMetadata,
        dataPoints: Array.from(groups.values()),
    };
}

export async function transformDataIfNeeded({
    workspaceId,
    intellsysBaseUrl,
    accessToken,
    queryClient,
    userTemplates,
    ouTemplates,
    resolvedDashboard,
    dataset,
    props,
    data,
    amaronEventContainerDispositions,
}: {
    workspaceId: Uuid;
    intellsysBaseUrl: string;
    accessToken: string;
    queryClient: QueryClient;
    users: Array<User>;
    userTemplates: Array<UserTemplate>;
    ouTemplates: Array<OrganizationalUnitTemplate>;
    resolvedDashboard: AnyResolvedDashboardDatasetsAndColumns;
    dataset: Dataset;
    props: DataResolverProps;
    data: RawData;
    amaronEventContainerDispositions?: Array<{id: Uuid; displayName: string}>;
}): Promise<Result<RawData>> {
    const datasetKey = Object.entries(resolvedDashboard.datasets).find(([, ds]) => ds.id === dataset.id)?.[0] as DashboardDatasetKey | undefined;
    const resolvedIds = datasetKey !== undefined ? (resolvedDashboard.columnIds[datasetKey] as Record<string, Uuid> | undefined) : undefined;
    const dwtIds = resolvedDashboard.columnIds.datasetWithTargets;
    const dwotIds = resolvedDashboard.columnIds.datasetWithoutTarget;
    const fkpiIds = resolvedDashboard.columnIds.enquiryToCallSaleFunnelKpis;

    const getColumnId = (columnIds: Record<string, Uuid> | undefined, columnKey: string): Uuid | undefined => columnIds?.[columnKey];
    const getColumnDisplayNames = (key: DashboardDatasetKey | undefined, columnKey: string): DisplayNameOrAliases | undefined => {
        if (key === undefined) {
            return undefined;
        }
        return (DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES[key] as Record<string, DisplayNameOrAliases>)[columnKey];
    };
    const displayNamesToArray = (displayNameOrAliases: DisplayNameOrAliases | undefined): string[] => {
        if (displayNameOrAliases === undefined) {
            return [];
        }
        return typeof displayNameOrAliases === "string" ? [displayNameOrAliases] : [...displayNameOrAliases];
    };
    const hasMatchingId = (items: Array<{id: Uuid}>, ids: Array<Uuid | undefined>): boolean => {
        const validIds = new Set(ids.filter((id): id is Uuid => id !== undefined));
        return items.some((item) => validIds.has(item.id));
    };

    const dwotJourneyId = dwotIds?.eventContainer;
    const dwotDispositionId = dwotIds?.disposition;
    if (
        dwotJourneyId !== undefined &&
        dwotDispositionId !== undefined &&
        props.metrics.some((m) => m.id === dwotJourneyId) &&
        props.dimensions.some((d) => d.id === dwotDispositionId)
    ) {
        const eventContainerDispositions = getDhiraaguEventContainerDispositions();
        data = transformColumnValues(
            data,
            DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithoutTarget.disposition,
            (value) => eventContainerDispositions.find((d) => d.id === value)?.displayName ?? "Others",
        );
    }

    const emotoradIds = resolvedDashboard.columnIds.emotoradPerformanceDataset;
    const emotoradDispositionId = emotoradIds?.disposition;
    if (datasetKey === "emotoradPerformanceDataset" && emotoradDispositionId !== undefined && props.dimensions.some((d) => d.id === emotoradDispositionId)) {
        data = transformColumnValues(data, DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.emotoradPerformanceDataset.disposition, (value) => {
            if (typeof value !== "string") {
                return "Unknown";
            }
            return getEmotoradDispositionL1Label(value as Uuid) ?? "Others";
        });
        data = aggregateRawDataByDimensions(data, [emotoradDispositionId]);
    }

    const amaronDispositionId =
        datasetKey === "amaronPerformanceDataset"
            ? resolvedDashboard.columnIds.amaronPerformanceDataset?.disposition
            : datasetKey === "amaronDailyPerformanceDataset"
              ? resolvedDashboard.columnIds.amaronDailyPerformanceDataset?.disposition
              : undefined;
    if (
        (datasetKey === "amaronPerformanceDataset" || datasetKey === "amaronDailyPerformanceDataset") &&
        amaronDispositionId !== undefined &&
        props.dimensions.some((d) => d.id === amaronDispositionId)
    ) {
        data = transformColumnValues(data, DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES[datasetKey].disposition, (value) =>
            resolveAmaronDashboardDispositionDisplayLabel(value, amaronEventContainerDispositions ?? []),
        );
    }

    const amaronFuelTypeId =
        datasetKey === "amaronPerformanceDataset"
            ? resolvedDashboard.columnIds.amaronPerformanceDataset?.fuelType
            : datasetKey === "amaronDailyPerformanceDataset"
              ? resolvedDashboard.columnIds.amaronDailyPerformanceDataset?.fuelType
              : undefined;
    if (
        (datasetKey === "amaronPerformanceDataset" || datasetKey === "amaronDailyPerformanceDataset") &&
        amaronFuelTypeId !== undefined &&
        props.dimensions.some((d) => d.id === amaronFuelTypeId)
    ) {
        const fuelTypeLookup = new Map(Object.values(amaronFuelTypes).map((ft) => [ft.id, ft.displayName]));
        data = transformColumnValues(data, DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES[datasetKey].fuelType, (value) =>
            typeof value === "string" ? (fuelTypeLookup.get(value as Uuid) ?? value) : "Unknown",
        );
    }

    const amaronServiceRequestedId =
        datasetKey === "amaronPerformanceDataset"
            ? resolvedDashboard.columnIds.amaronPerformanceDataset?.serviceRequested
            : datasetKey === "amaronDailyPerformanceDataset"
              ? resolvedDashboard.columnIds.amaronDailyPerformanceDataset?.serviceRequested
              : undefined;
    if (
        (datasetKey === "amaronPerformanceDataset" || datasetKey === "amaronDailyPerformanceDataset") &&
        amaronServiceRequestedId !== undefined &&
        props.dimensions.some((d) => d.id === amaronServiceRequestedId)
    ) {
        const serviceRequestedLookup = new Map(Object.values(amaronServiceRequestedTypes).map((sr) => [sr.id, sr.displayName]));
        data = transformColumnValues(data, DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES[datasetKey].serviceRequested, (value) =>
            typeof value === "string" ? (serviceRequestedLookup.get(value as Uuid) ?? value) : "Unknown",
        );
    }

    const productDimensionIds = [getColumnId(resolvedIds, "productCatalogue"), dwtIds?.product];
    const productColumnDisplayNames = [
        ...displayNamesToArray(getColumnDisplayNames(datasetKey, "productCatalogue")),
        ...(dwtIds?.product !== undefined ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithTargets.product) : []),
    ];
    if (productColumnDisplayNames.length > 0 && hasMatchingId(props.dimensions, productDimensionIds)) {
        const productCataloguesResult = await queryClient.ensureQueryData({
            queryKey: ["productCatalogues-widgets-dashboard", workspaceId],
            queryFn: async () => {
                return getProductCatalogues({
                    workspaceId: workspaceId,
                    intellsysBaseUrl: intellsysBaseUrl,
                    accessToken: accessToken,
                    ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
                });
            },
            staleTime: Infinity,
            retry: false,
        });
        if (productCataloguesResult.success === false) {
            throw new Error("Failed to fetch product catalogues");
        }
        const productCatalogues = productCataloguesResult.data;
        data = transformColumnValues(data, productColumnDisplayNames, (value) => productCatalogues.find((pc) => pc.id === value)?.id ?? "Text", "Products");
    }

    // const dwtIds = resolvedDashboard.columnIds.datasetWithTargets;
    // if (props.dimensions.some((d) => d.id === dwtIds.userDistributionScoreId)) {
    //     data = transformColumnValues(
    //         data,
    //         DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithTargets.userId,
    //         (value) => getUserDisplayName(value as Uuid, users, userTemplates),
    //         "User",
    //     );
    // }

    const ouColumnKeys = ["ou", "region", "atoll", "roc", "island"] as const;
    const ouColumnIds = ouColumnKeys.map((columnKey) => getColumnId(resolvedIds, columnKey));
    const needsOuTransform = ouColumnIds.some((id) => props.dimensions.some((d) => d.id === id));

    if (needsOuTransform) {
        const organizationalUnitsResult = await getOrganizationalUnitsWithCaching({
            workspaceId,
            intellsysBaseUrl,
            accessToken,
            queryClient,
            ...CACHEABLE_WORKSPACE_ITEM_FULL_READ_INPUT,
        });
        if (organizationalUnitsResult.success !== true) {
            throw organizationalUnitsResult.error;
        }
        const organizationalUnits = organizationalUnitsResult.data;

        const resolveOuDisplayName = (value: unknown): string => {
            const ou = organizationalUnits.find((o) => o.id === String(value));
            if (ou === undefined) {
                return typeof value === "string" ? value : "";
            }
            const template = ouTemplates.find((t) => t.id === ou.templateId);
            return template !== undefined ? processOttopilotStringTemplate(template.instanceDisplayNameTemplate, ou.additionalColumnValues) : String(ou.id);
        };

        const ouDimensionColumns: Array<[Uuid | undefined, DisplayNameOrAliases | undefined]> = ouColumnKeys.map((columnKey) => [
            getColumnId(resolvedIds, columnKey),
            getColumnDisplayNames(datasetKey, columnKey),
        ]);

        for (const [columnId, displayName] of ouDimensionColumns) {
            if (columnId !== undefined && displayName !== undefined && props.dimensions.some((d) => d.id === columnId)) {
                data = transformColumnValues(data, displayName, resolveOuDisplayName);
            }
        }
    }

    const statusDimensionIds = [dwtIds?.status, dwotIds?.status];
    const statusDisplayNames = [
        ...(dwtIds?.status !== undefined ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithTargets.status) : []),
        ...(dwotIds?.status !== undefined ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithoutTarget.status) : []),
    ];
    if (
        statusDisplayNames.length > 0 &&
        hasMatchingId(props.dimensions, statusDimensionIds) &&
        hasMatchingId(props.metrics, [dwtIds?.eventContainer, dwotIds?.eventContainer])
    ) {
        data = transformColumnValues(data, statusDisplayNames, (value) => eventContainerStatusToDisplayName(value as OttopilotEventContainerStatus));
    }

    if (
        statusDisplayNames.length > 0 &&
        hasMatchingId(props.dimensions, statusDimensionIds) &&
        hasMatchingId(props.metrics, [dwtIds?.calls, dwotIds?.calls, dwtIds?.sales, dwotIds?.sales, dwtIds?.enquiry, dwotIds?.enquiry])
    ) {
        data = transformColumnValues(data, statusDisplayNames, (value) => eventStatusToDisplayName(value as OttopilotEventStatus));
    }

    const dwtStatusDisplayNames = dwtIds?.status !== undefined ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithTargets.status) : [];
    if (dwtStatusDisplayNames.length > 0 && hasMatchingId(props.dimensions, [dwtIds?.status]) && hasMatchingId(props.metrics, [dwtIds?.btlActivities])) {
        data = transformColumnValues(data, dwtStatusDisplayNames, (value) => btlEventStatusToDisplayName(value as OttopilotBtlActivityStatus));
    }

    const eventTemplateDisplayNames = [
        ...(dwtIds?.eventTemplate !== undefined ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithTargets.eventTemplate) : []),
        ...(dwotIds?.eventTemplate !== undefined ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithoutTarget.eventTemplate) : []),
    ];
    if (eventTemplateDisplayNames.length > 0 && hasMatchingId(props.dimensions, [dwtIds?.eventTemplate, dwotIds?.eventTemplate])) {
        data = transformColumnValues(data, eventTemplateDisplayNames, (value) =>
            typeof value === "string" ? (Object.values(lastEventTypes).find((t) => t.id === value)?.displayName ?? value) : "Event",
        );
    }

    const btlActivityTemplateColumnId = getColumnId(resolvedIds, "btlActivityTemplate");
    const btlActivityTemplateDisplayNames = [
        ...(dwotIds?.btlActivityTemplate !== undefined
            ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithoutTarget.btlActivityTemplate)
            : []),
        ...(dwtIds?.btlActivityTemplate !== undefined
            ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.datasetWithTargets.btlActivityTemplate)
            : []),
        ...(fkpiIds?.btlActivityTemplate !== undefined
            ? displayNamesToArray(DASHBOARD_DATASET_COLUMN_DISPLAY_NAMES.enquiryToCallSaleFunnelKpis.btlActivityTemplate)
            : []),
    ];
    if (
        btlActivityTemplateDisplayNames.length > 0 &&
        btlActivityTemplateColumnId !== undefined &&
        props.dimensions.some((d) => d.id === btlActivityTemplateColumnId)
    ) {
        const btlActivityTemplatesResult = await queryClient.ensureQueryData<Array<BtlActivityTemplateData>>({
            queryKey: ["btlActivityTemplates-widgets-dashboard", workspaceId],
            queryFn: async () => {
                const result = await getBtlActivityTemplates({
                    workspaceId,
                    offset: 0,
                    limit: 9999,
                    filterConfiguration: {
                        revisionId: uniqueUuid`a1b2c3d4-e5f6-7890-abcd-ef1234567890`,
                        lineItems: [],
                    },
                    sortConfiguration: {
                        revisionId: uniqueUuid`b2c3d4e5-f6a7-8901-bcde-f12345678901`,
                        lineItems: [],
                    },
                    intellsysBaseUrl,
                    accessToken,
                });
                if (result.success !== true) {
                    throw result.error;
                }
                return result.data;
            },
            staleTime: Infinity,
            retry: false,
        });
        data = transformColumnValues(
            data,
            btlActivityTemplateDisplayNames,
            (value) => btlActivityTemplatesResult.find((t) => t.id === value)?.displayName ?? (typeof value === "string" ? value : ""),
        );
    }

    const userTemplateColumnId = getColumnId(resolvedIds, "userTemplate");
    const userTemplateDisplayNames = (Object.entries(resolvedDashboard.columnIds) as Array<[DashboardDatasetKey, Record<string, Uuid>]>).flatMap(
        ([key, ids]) => (getColumnId(ids, "userTemplate") !== undefined ? displayNamesToArray(getColumnDisplayNames(key, "userTemplate")) : []),
    );
    if (userTemplateDisplayNames.length > 0 && userTemplateColumnId !== undefined && props.dimensions.some((d) => d.id === userTemplateColumnId)) {
        data = transformColumnValues(
            data,
            userTemplateDisplayNames,
            (value) => userTemplates.find((t) => t.id === value)?.displayName ?? (typeof value === "string" ? value : ""),
        );
    }

    // if (props.metrics.some(m => m.id === resolvedDashboard.columnIds.datasetWithoutTarget.enquiryToCallsDropOffPercent)) {
    //     console.log("enquiryToCallsDropOffPercent", data);
    // }

    return dataResult(data);
}
